import { useState, useCallback, useEffect } from 'react';
import { Grid, Button, TextField } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { FileUploaded } from '../../../../models/fileUpload';
import { fileUploadApiKeyStore } from '../../../../api/file-upload-key-store';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { useUploadData } from '../../../../hooks/useUploadData';
import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { FormInput } from '../../../../components/hook-form/form-input.component';
import { MutationKeyConfig } from '../../../../api/api-helper';
import { useSaveData } from '../../../../hooks/useSaveData';
import { GuardedComponent } from '../../../../components/guarded-component';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { useAuthorize } from '../../../../hooks/use-authorize';

const styling = {
  dropZone: {
    padding: '27px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
  },
  perdropZone: {
    padding: '27px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    display: 'flex',
    justifyContent: 'center',
    borderRadius: '4px',
  },
  iconButtonStyle: {
    minHeight: 75,
  },
  iconStyle: { width: '40px', height: '40px' },
};

interface FileRequestArgs {
  projectID?: number;
  officeID?: number;
  customerID?: number;
  tempEntityID?: any;
}

interface FilesDialogProps {
  fileRequestArgs: FileRequestArgs;
  onUploaded: (data: FileUploaded) => void;
}

export const UploadFileForm = ({ fileRequestArgs, onUploaded }: FilesDialogProps) => {
  const [files, setFiles] = useState<any>();
  const form = useReactHookForm({ defaultValues: { fileID: '', fileName: '', fileType: '', description: '' } });
  const { isLoading, mutateAsync } = useSaveData<FileUploaded>(fileUploadApiKeyStore.uploadFile());

  const handleDrop = useCallback((acceptedFiles: any) => {
    setFiles(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const submitHanlder = async (value: any) => {
    if (files) {
      const formData = new FormData();
      formData.append('fileID', value?.fileID);
      formData.append('UploadedFile', files ?? '');
      formData.append('FileName', files ? files?.name : value?.fileName);
      formData.append('FileType', files ? files?.type : value?.fileType);
      formData.append('Description', value.description ?? '');
      formData.append('FileSection', 'office');

      if (fileRequestArgs) {
        if (fileRequestArgs.projectID) {
          formData.append('ProjectID', fileRequestArgs.projectID.toString());
        }
        if (fileRequestArgs.officeID) {
          formData.append('OfficeID', fileRequestArgs.officeID.toString());
        }
        if (fileRequestArgs.customerID) {
          formData.append('CustomerID', fileRequestArgs.customerID.toString());
        }
        if (fileRequestArgs.tempEntityID) {
          formData.append('TempEntityID', fileRequestArgs.tempEntityID.toString());
        }
      }

      const data = await mutateAsync({ data: formData });
      onUploaded(data?.data);
      setFiles(undefined);
      form.reset({ fileID: '', fileName: '', fileType: '', description: '' });
    }
  };

  const handleSubmitClick = () => {
    form.handleSubmit(submitHanlder)();
  };

  const isFileEditPermission = useAuthorize([UserPermissionName?.UPLOAD_FILE]);
  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <ReactHookForm formName="UPLOAD_FILE_FORM" onSubmit={submitHanlder} formContextProps={form}>
      <LoadingIndicator isLoading={isLoading} />
      <Grid container spacing={2}>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          {!isFileEditPermission ? (
            <div style={styling.perdropZone}>
              <input disabled={true} {...getInputProps()} />
              <div>
                <p>You are not able to upload a new file.</p>
              </div>
            </div>
          ) : (
            <div style={styling.dropZone} {...getRootProps()}>
              <input disabled={true} {...getInputProps()} />
              {isDragActive ? (
                <p>Drop the files here ...</p>
              ) : (
                <div>
                  {files ? (
                    <p>{`${files.name} are selected to upload`}</p>
                  ) : (
                    <p>Drag and drop a file here, or click to select a file</p>
                  )}
                </div>
              )}
            </div>
          )}
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <Grid container style={{ display: 'flex' }}>
            <Grid item style={{ width: 'calc(100% - 100px', marginRight: '15px' }}>
              <FormInput
                label="Description"
                name="description"
                multiline={true}
                disabled={!isFileEditPermission}
                size={2.5}
              />
            </Grid>
            <Grid style={{ width: '85px' }} className="d-flex">
              <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                <Button type="button" variant="outlined" style={styling.iconButtonStyle} onClick={handleSubmitClick} disabled={isReadOnlyPermission}>
                  UPLOAD
                </Button>
              </GuardedComponent>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ReactHookForm>
  );
};
